import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, AlertTriangle } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  connectScheduling,
  disconnectScheduling,
  getSchedulingIntegration
} from '../../services/api/implementation/impl';
import {
  SCHEDULING_INTEGRATION_QUERY_KEY,
  isSchedulingConnected,
  providerLabel
} from '../../utils/schedulingAccess';
import toast from 'react-hot-toast';

export default function SchedulingSettingsModal({ onClose }) {
  const queryClient = useQueryClient();
  const [scheduleId, setScheduleId] = useState('');
  const [vendorPhoneNumberId, setVendorPhoneNumberId] = useState('');
  const [vendorUserId, setVendorUserId] = useState('');
  const [scheduleName, setScheduleName] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [timezone, setTimezone] = useState('');
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const { data: integration, isLoading } = useQuery({
    queryKey: SCHEDULING_INTEGRATION_QUERY_KEY,
    queryFn: getSchedulingIntegration,
    retry: false
  });

  const connected = isSchedulingConnected(integration);

  useEffect(() => {
    if (integration?.scheduleId) setScheduleId(integration.scheduleId);
    if (integration?.vendorPhoneNumberId) {
      setVendorPhoneNumberId(integration.vendorPhoneNumberId);
    }
    if (integration?.vendorUserId) setVendorUserId(integration.vendorUserId);
    if (integration?.scheduleName) setScheduleName(integration.scheduleName);
    if (integration?.durationMinutes) {
      setDurationMinutes(String(integration.durationMinutes));
    }
    if (integration?.timezone) setTimezone(integration.timezone);
  }, [integration]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: SCHEDULING_INTEGRATION_QUERY_KEY });
  };

  const connectMutation = useMutation({
    mutationFn: () =>
      connectScheduling({
        provider: 'pinggo',
        api_key: null,
        base_url: null,
        timezone: timezone.trim() || null,
        schedule_id: scheduleId.trim(),
        vendor_phone_number_id: vendorPhoneNumberId.trim(),
        vendor_user_id: vendorUserId.trim() || null,
        schedule_name: scheduleName.trim() || null,
        duration_minutes: durationMinutes.trim() ? Number(durationMinutes) : null
      }),
    onSuccess: () => {
      toast.success('WhatsApp booking connected');
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not connect');
    }
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectScheduling,
    onSuccess: () => {
      toast.success('Disconnected');
      setConfirmingDisconnect(false);
      invalidateAll();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not disconnect');
    }
  });

  const busy = connectMutation.isPending || disconnectMutation.isPending;
  const canSubmit = Boolean(scheduleId.trim() && vendorPhoneNumberId.trim());

  const handleConnect = () => {
    if (!scheduleId.trim()) {
      toast.error('Paste the WhatsApp booking schedule ID.');
      return;
    }
    if (!vendorPhoneNumberId.trim()) {
      toast.error("Paste the vendor's WhatsApp phone number ID.");
      return;
    }
    connectMutation.mutate();
  };

  return (
    <Modal isDirty confirmOnClose name="Scheduling" onClose={onClose} width={54}>
      <div className="space-y-6 p-1">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-teal-50 p-2 text-[#1E4A53]">
            <CalendarClock className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">WhatsApp scheduling</h2>
            <p className="text-sm text-gray-600">
              Connect WhatsApp booking so leads can pick a demo date and time in WhatsApp.
            </p>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {connected ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <p className="font-medium text-gray-900">
                  Connected to {providerLabel(integration.provider)}
                </p>
                {integration.accountLabel ? (
                  <p className="mt-1 text-xs text-gray-600">{integration.accountLabel}</p>
                ) : null}
              </div>
            ) : null}

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Schedule ID
              </label>
              <input
                type="text"
                value={scheduleId}
                onChange={(e) => setScheduleId(e.target.value)}
                placeholder="Schedule id"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Vendor WhatsApp phone number ID
              </label>
              <input
                type="text"
                value={vendorPhoneNumberId}
                onChange={(e) => setVendorPhoneNumberId(e.target.value)}
                placeholder="vendorPhoneNumberId"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Not the +91 number itself — the ID your WhatsApp provider shows for that number.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Vendor user ID <span className="normal-case text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={vendorUserId}
                onChange={(e) => setVendorUserId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Schedule name <span className="normal-case text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                placeholder="Demo Webinar"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Duration (minutes) <span className="normal-case text-gray-400">(optional)</span>
              </label>
              <input
                type="number"
                min={1}
                max={1440}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                placeholder="Must match the booking schedule"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                Schedule timezone
              </label>
              <input
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Asia/Kolkata"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="mt-1 flex items-start gap-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                Must match the timezone on the booking schedule.
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
              {connected ? (
                confirmingDisconnect ? (
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-700">
                      Disconnect? Past meetings stay on the lead timeline.
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      className="font-semibold text-red-700 underline disabled:opacity-50"
                      onClick={() => disconnectMutation.mutate()}>
                      {disconnectMutation.isPending ? 'Disconnecting…' : 'Yes, disconnect'}
                    </button>
                    <button
                      type="button"
                      className="font-semibold text-gray-600 underline"
                      onClick={() => setConfirmingDisconnect(false)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    className="text-xs font-semibold text-red-700 underline disabled:opacity-50"
                    onClick={() => setConfirmingDisconnect(true)}>
                    Disconnect
                  </button>
                )
              ) : (
                <span />
              )}

              <Button
                displayType="primary"
                isLoading={connectMutation.isPending}
                disabled={busy || !canSubmit}
                onClick={handleConnect}>
                {connected ? 'Update connection' : 'Connect'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
